/**
 * Utility functions for common operations
 */

// Debounce function
export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Format date
export const formatDate = (date: string | Date, locale = "ru-RU"): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleDateString(locale);
};

// Format currency
export const formatCurrency = (
  amount: number,
  currency = "RUB",
  locale = "ru-RU",
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(amount);
};

// Format price number with locale (uses comma as decimal separator for ru-RU)
export const formatPrice = (amount: number, locale = "ru-RU"): string => {
  return amount.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Generate random ID
export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

// Capitalize first letter
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Check if string is empty or whitespace
export const isEmpty = (str: string): boolean => {
  return !str || str.trim().length === 0;
};

// Sleep function for testing/development
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

// Local storage helpers
export const storage = {
  get: <T>(key: string): T | null => {
    if (typeof window === "undefined") return null;

    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },

  set: <T>(key: string, value: T): void => {
    if (typeof window === "undefined") return;

    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore storage errors
    }
  },

  remove: (key: string): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(key);
  },

  clear: (): void => {
    if (typeof window === "undefined") return;
    localStorage.clear();
  },
};

export function formatChange(pct: number | null | undefined): string {
  const val = Number(pct ?? 0);
  if (!isFinite(val)) return "—";
  return `${val >= 0 ? "+" : ""}${val.toFixed(1)}%`;
}

export const formatMoney = (value: number) =>
  value.toLocaleString("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  });

export const formatMoneyRub = formatMoney;
export const formatMoneyToRub = (value: number) => {
  const rubs = value / 100;
  return rubs.toLocaleString("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  });
};

/** Собирает полный URL для файла из uploads: `{domain}/uploads/{filepath}` */
export const resolveUploadUrl = (filepath: string, domain = ""): string => {
  if (!filepath) return "";
  if (filepath.startsWith("http://") || filepath.startsWith("https://")) {
    return filepath;
  }

  const base = domain.replace(/\/$/, "");
  const normalizedPath = filepath.replace(/^\/+/, "");

  if (normalizedPath.startsWith("uploads/")) {
    return `${base}/${normalizedPath}`;
  }

  return `${base}/uploads/${normalizedPath}`;
};
